import { useId } from 'react';
import { cn } from '../../lib/cn';
import type { Chalk } from '../../lib/chalk';
import { CHALK } from '../../lib/chalk';

/**
 * A hand-drawn chalk stroke. The turbulence filter roughens the edge so it reads as chalk,
 * not as a vector line.
 */
export function ChalkUnderline({ color = 'yellow', className }: { color?: Chalk; className?: string }) {
  const id = useId().replace(/:/g, '');
  return (
    <svg viewBox="0 0 220 14" preserveAspectRatio="none" className={cn('block h-3 w-full', className)} aria-hidden focusable="false">
      <defs>
        <filter id={`chalk-${id}`} x="-5%" y="-50%" width="110%" height="200%">
          <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed="7" result="noise" />
          <feDisplacementMap in="SourceGraphic" in2="noise" scale="2.2" />
        </filter>
      </defs>
      <path
        d="M3 9 C 40 4, 70 11, 110 7 S 180 4, 217 8"
        fill="none"
        stroke={CHALK[color].stroke}
        strokeWidth="3.2"
        strokeLinecap="round"
        strokeDasharray="160 6 40 4"
        filter={`url(#chalk-${id})`}
        opacity="0.9"
      />
    </svg>
  );
}

/** Handwritten page title with a coloured chalk underline. */
export function ChalkTitle({ children, color = 'yellow', as: Tag = 'h1' }: { children: string; color?: Chalk; as?: 'h1' | 'h2' }) {
  return (
    <Tag className="chalk-title inline-block text-[2.6rem] text-ink sm:text-5xl">
      {children}
      <ChalkUnderline color={color} className="-mt-0.5" />
    </Tag>
  );
}

/**
 * Chalk tally marks (groups of five) for small counts — how you'd count on a real board.
 * Large counts fall back to a chalk bar so the row never overflows.
 */
export function Tally({ count, max, color }: { count: number; max: number; color: string }) {
  if (count === 0) return <span className="block h-5 w-full border-b border-dashed border-line/70" aria-hidden />;
  if (count > 25) {
    return (
      <span className="block h-2 w-full rounded-full bg-raised" aria-hidden>
        <span className="block h-2 rounded-full" style={{ width: `${Math.max(6, (count / max) * 100)}%`, background: color }} />
      </span>
    );
  }
  const groups = Math.ceil(count / 5);
  return (
    <svg viewBox={`0 0 ${groups * 30} 22`} className="h-5" style={{ width: groups * 30 }} aria-hidden focusable="false">
      {Array.from({ length: groups }, (_, g) => {
        const inGroup = Math.min(5, count - g * 5);
        const x0 = g * 30 + 3;
        return (
          <g key={g} stroke={color} strokeWidth="2.4" strokeLinecap="round" opacity="0.92">
            {Array.from({ length: Math.min(4, inGroup) }, (_, i) => (
              <line key={i} x1={x0 + i * 5.5} y1={3 + (i % 2)} x2={x0 + i * 5.5 + 0.8} y2={19 - (i % 2)} />
            ))}
            {inGroup === 5 && <line x1={x0 - 2} y1={16} x2={x0 + 20} y2={5} />}
          </g>
        );
      })}
    </svg>
  );
}
